import { BoxProps, useToken } from '@chakra-ui/react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  LabelList,
  Legend,
  Tooltip,
} from 'recharts'

import { CustomLegend } from '../StackedChart/customChartComponents/CustomLegend'
import { FloatingChartTooltipShell } from '../chartTooltip/ChartTooltipPointerContext'
import type { FloatingCartesianExtraRow } from '../chartTooltip/FloatingCartesianChartTooltip'
import { FloatingCartesianChartTooltip } from '../chartTooltip/FloatingCartesianChartTooltip'
import { CategoryAxisTick } from '../common/CategoryAxisTick'
import { GroupedVerticalBarTotalsLayer } from '../common/GroupedVerticalBarTotalsLayer'
import { RechartsWrapper } from '../common/RechartsWrapper'
import {
  BAR_BACKGROUND_COLOR,
  BAR_CORNER_RADIUS,
  CHART_GRID_STROKE,
  CHART_HEIGHT,
  CHART_MIN_WIDTH,
  MIN_WIDTH_PER_ITEM,
  SNAPSHOT_ANGLED_AXIS_BAND_HEIGHT,
  SNAPSHOT_ANGLED_X_AXIS,
  SNAPSHOT_CHART_HEIGHT,
  SNAPSHOT_CHART_MARGIN,
  SNAPSHOT_VERTICAL_BAR_AXIS_PADDING_PX,
  SNAPSHOT_VERTICAL_BAR_CATEGORY_GAP,
  SNAPSHOT_VERTICAL_BAR_SIZE,
  VERTICAL_BAR_CLUSTER_WIDTH_PX,
  VERTICAL_BAR_INTER_CATEGORY_GAP_PX,
} from '../common/chart.constants'
import { ChartData } from '../common/chart.types'
import { formatChartDataLabelValue } from '../common/chartFormat'
import { ChartGrid } from '../common/chartGridLines'
import { useCustomRecharts } from '../common/useCustomRecharts'
import {
  StackedVerticalTotalLabel,
  type VerticalBarTotalLabelProps,
} from '../common/verticalBarTotalLabels'

export type SeriesItem = {
  name: string
  dataKey: string
  color: string
}

export type VerticalBarChartProps = BoxProps & {
  chartData: ChartData
  colorScheme?: 'positive' | 'negative' | 'neutral'
  series?: SeriesItem[]
  /** Stack bars on top of each other instead of side-by-side. */
  stacked?: boolean
  /** Override chart height (px). */
  chartHeight?: number
  /** Hide the built-in legend (render it externally instead). */
  hideLegend?: boolean
  /** Enable horizontal scrolling when there are many data points. */
  horizontalScroll?: boolean
  /** Extra row keys on each data point (e.g. cube `count`) for the tooltip. */
  floatingTooltipExtraRows?: FloatingCartesianExtraRow[]
  /** When true, show totals above each category (grouped/stacked) or above single bars. */
  showBarValueLabels?: boolean
  /** Homepage snapshot charts: wider bars, percent category gap, no bar stroke. */
  variant?: 'default' | 'snapshot'
}

export const VerticalBarChart = ({
  chartData,
  colorScheme = 'neutral',
  series,
  stacked = false,
  chartHeight,
  hideLegend = false,
  horizontalScroll = false,
  floatingTooltipExtraRows,
  showBarValueLabels = false,
  variant = 'default',
  ...props
}: VerticalBarChartProps) => {
  const isSnapshot = variant === 'snapshot'
  const { XAxis, YAxis, chartWrapperRef } = useCustomRecharts({
    chartData,
    series,
    stacked,
    variant,
  })
  const blue = useToken('colors.presets', 'blue.50')
  const red = useToken('colors.presets', 'pink.70')

  if (!series) {
    const colorSchemes = {
      positive: { positive: blue, negative: red },
      negative: { positive: red, negative: blue },
      neutral: { positive: blue, negative: blue },
    }

    const colors = colorSchemes[colorScheme]

    chartData.chartValues = chartData?.chartValues?.map((entry: any) => ({
      ...entry,
      fill: entry.value >= 0 ? colors.positive : colors.negative,
    }))
  }

  const itemCount = chartData.chartValues?.length ?? 0
  const Y_AXIS_PADDING = 60
  const scrollableWidth =
    horizontalScroll && itemCount > 0
      ? itemCount * MIN_WIDTH_PER_ITEM + Y_AXIS_PADDING
      : undefined

  const floatingSeriesMeta =
    series?.map(s => ({
      dataKey: s.dataKey,
      name: s.name,
      color: s.color,
    })) ?? []

  const seriesDataKeys = series?.map(s => s.dataKey) ?? []
  const chartRows = (chartData.chartValues ?? []) as Record<string, unknown>[]

  const seriesCount = series?.length ?? 0
  // Snapshot homepage bars only (`chartVariant: 'snapshot'`). Default = Port-to-Port layout.
  const barWidthPx = isSnapshot
    ? (chartData.snapshotBarWidth ?? SNAPSHOT_VERTICAL_BAR_SIZE)
    : series && seriesCount > 0
      ? stacked
        ? VERTICAL_BAR_CLUSTER_WIDTH_PX
        : VERTICAL_BAR_CLUSTER_WIDTH_PX / seriesCount
      : VERTICAL_BAR_CLUSTER_WIDTH_PX

  const topMarginForBarLabels = showBarValueLabels ? 28 : 4
  const categoryAxisLabelLayout = chartData.categoryAxisLabelLayout ?? 'angled'
  const snapshotAngledAxisBand = chartData.snapshotAngledAxisBand ?? 'medium'
  const xAxisHeight = isSnapshot
    ? categoryAxisLabelLayout === 'flat'
      ? 80
      : SNAPSHOT_ANGLED_AXIS_BAND_HEIGHT[snapshotAngledAxisBand]
    : categoryAxisLabelLayout === 'flat'
      ? 80
      : 78

  const barChart = (
    <BarChart
      data={chartData.chartValues ?? []}
      margin={isSnapshot ? SNAPSHOT_CHART_MARGIN : undefined}
      {...(isSnapshot && series && !stacked
        ? { barCategoryGap: SNAPSHOT_VERTICAL_BAR_CATEGORY_GAP, barGap: 0 }
        : series && !stacked && { barCategoryGap: 2, barGap: 2 })}
      {...(stacked && { barCategoryGap: 6 })}
    >
      {XAxis({
        dataKey: chartData.nameKey ?? 'key',
        padding: isSnapshot
          ? {
              left: SNAPSHOT_VERTICAL_BAR_AXIS_PADDING_PX,
              right: SNAPSHOT_VERTICAL_BAR_AXIS_PADDING_PX,
            }
          : {
              left: VERTICAL_BAR_INTER_CATEGORY_GAP_PX / 2,
              right: VERTICAL_BAR_INTER_CATEGORY_GAP_PX / 2,
            },
        tick: (
          <CategoryAxisTick
            variant={variant}
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            dateFormat={chartData.categoryAxisDateFormat}
            dateLayout={categoryAxisLabelLayout}
            {...(categoryAxisLabelLayout === 'angled' && {
              unparseableTruncate: {
                maxLength: 24,
                angle: -30,
                textAnchor: 'end',
                fontWeight: 500,
              },
            })}
          />
        ),
        height: xAxisHeight,
        ...(isSnapshot && categoryAxisLabelLayout === 'angled'
          ? SNAPSHOT_ANGLED_X_AXIS
          : {}),
        ...(horizontalScroll && { padding: { left: 10, right: 16 } }),
      })}
      {YAxis({
        padding: { top: topMarginForBarLabels, bottom: 0 },
        ...(horizontalScroll && !isSnapshot && { width: 35 }),
      })}
      <CartesianGrid
        stroke={CHART_GRID_STROKE}
        horizontal={ChartGrid.horizontalLine}
        syncWithTicks
        vertical={false}
      />
      <Tooltip
        shared={false}
        cursor={false}
        content={
          <FloatingCartesianChartTooltip
            floatingLayout="bar"
            nameKey={chartData.nameKey ?? 'key'}
            series={floatingSeriesMeta}
            extraRows={floatingTooltipExtraRows}
            categoryAxisDateFormat={chartData.categoryAxisDateFormat}
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            valueFormat={chartData.valueFormat}
          />
        }
      />
      {series ? (
        <>
          {!hideLegend && (
            <Legend
              content={<CustomLegend />}
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: 10 }}
            />
          )}
          {series.map((s, index) => (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name}
              fill={s.color}
              {...(!isSnapshot && {
                stroke: 'rgba(255, 255, 255, 0.22)',
                strokeWidth: 1,
              })}
              {...(stacked && { stackId: 'stack' })}
              radius={
                stacked && index < series.length - 1
                  ? [0, 0, 0, 0]
                  : [BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0, 0]
              }
              {...(isSnapshot
                ? { maxBarSize: barWidthPx }
                : { barSize: barWidthPx })}
              isAnimationActive={false}
            >
              {showBarValueLabels && stacked && index === series.length - 1 ? (
                <LabelList
                  content={props => (
                    <StackedVerticalTotalLabel
                      {...(props as VerticalBarTotalLabelProps)}
                      rows={chartRows}
                      seriesDataKeys={seriesDataKeys}
                      valueFormat={chartData.valueFormat}
                    />
                  )}
                />
              ) : null}
            </Bar>
          ))}
          {showBarValueLabels && !stacked ? (
            <Customized
              component={GroupedVerticalBarTotalsLayer}
              rows={chartRows}
              seriesDataKeys={seriesDataKeys}
              valueFormat={chartData.valueFormat}
            />
          ) : null}
        </>
      ) : (
        <Bar
          dataKey="value"
          fill="fill"
          barSize={barWidthPx}
          radius={[BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0, 0]}
          background={{
            fill: BAR_BACKGROUND_COLOR,
            radius: BAR_CORNER_RADIUS,
          }}
          isAnimationActive={false}
        >
          {showBarValueLabels ? (
            <LabelList
              dataKey="value"
              position="top"
              offset={8}
              fill="white"
              fontSize={12}
              fontWeight={700}
              formatter={(v: number) =>
                formatChartDataLabelValue(v, chartData.valueFormat)
              }
            />
          ) : null}
        </Bar>
      )}
    </BarChart>
  )

  const resolvedChartHeight =
    chartHeight ?? (isSnapshot ? SNAPSHOT_CHART_HEIGHT : CHART_HEIGHT)

  const wrapperCommon = {
    ref: chartWrapperRef,
    yAxisLabel: chartData.yAxisLabel,
    xAxisLabel: chartData.xAxisLabel,
    scrollableWidth,
    chartHeight: resolvedChartHeight,
    minW: isSnapshot ? 0 : `${CHART_MIN_WIDTH}px`,
    variant,
    ...props,
  }

  return (
    <FloatingChartTooltipShell>
      {mouseHandlers => (
        <RechartsWrapper
          {...wrapperCommon}
          chartAreaMouseTracking={mouseHandlers}
        >
          {barChart}
        </RechartsWrapper>
      )}
    </FloatingChartTooltipShell>
  )
}
